import { createMachine, assign } from 'xstate'
import { hasMapTooltipTarget } from '~/util/mapTooltipQuery'
import { getTrackedParamsFromURL } from '~/util/trackedURLParams'

const { coordinates, wavePointName } = getTrackedParamsFromURL()

export const mapTooltipMachine = createMachine(
  {
    id: 'mapTooltip',
    types: {} as {
      context: {
        coordinates: { lng: number; lat: number } | null
        screenPosition: { x: number; y: number } | null
        wavePointName: string | null
        buildingOcctype: string | null
        buildingCategories: string[] | null
        /** Full building props so damages can recompute when scenario changes. */
        buildingProperties: GeoJSON.GeoJsonProperties | null
        buildingSource: string | null
        totalDamages: number | null
        population: number | null
        floodDepthContour: number | null
        floodDepthLabel: string | null
        adaptationType: string | null
      }
      events:
        | {
            type: 'MOUSE_MOVE'
            x: number
            y: number
            lat: number
            lng: number
            wavePointName?: string | null
            adaptationType?: string | null
            buildingOcctype?: string | null
            buildingCategories?: string[] | null
            buildingProperties?: GeoJSON.GeoJsonProperties | null
            buildingSource?: string | null
            totalDamages?: number | null
            population?: number | null
            floodDepthContour?: number | null
            floodDepthLabel?: string | null
            features?: unknown
          }
        | {
            type: 'MAP_CLICKED'
            x: number
            y: number
            lat: number
            lng: number
            wavePointName?: string | null
            adaptationType?: string | null
            buildingOcctype?: string | null
            buildingCategories?: string[] | null
            buildingProperties?: GeoJSON.GeoJsonProperties | null
            buildingSource?: string | null
            totalDamages?: number | null
            population?: number | null
            floodDepthContour?: number | null
            floodDepthLabel?: string | null
            features?: unknown
          }
        | { type: 'UPDATE_POSITION'; x: number; y: number }
        | { type: 'MAP_MOVED' }
        | { type: 'MOUSE_LEAVE' }
        | { type: 'ADAPTATION_CHANGED' }
        | { type: 'CLOSE_TOOLTIP' }
    },

    context: {
      coordinates: coordinates ?? null,
      screenPosition: null,
      wavePointName: wavePointName ?? null,
      buildingOcctype: null,
      buildingCategories: null,
      buildingProperties: null,
      buildingSource: null,
      totalDamages: null,
      population: null,
      floodDepthContour: null,
      floodDepthLabel: null,
      adaptationType: null,
    },

    initial: coordinates ? 'visible_from_click' : 'closed',

    on: {
      ADAPTATION_CHANGED: '.closed',
      CLOSE_TOOLTIP: '.closed',
    },

    states: {
      closed: {
        on: {
          MOUSE_MOVE: {
            target: 'hovering',
            guard: 'hasTooltipTarget',
            actions: ['setContext'],
          },
          MAP_CLICKED: {
            target: 'visible_from_click',
            guard: 'hasTooltipTarget',
            actions: ['setContext'],
          },
        },
      },

      hovering: {
        after: {
          // Pause required before hover tooltip (matches NBS Adapts / flood-depth AC).
          1000: {
            target: 'visible_from_hover',
          },
        },
        on: {
          MOUSE_MOVE: [
            {
              target: 'hovering',
              actions: ['setContext'],
              guard: 'hasTooltipTarget',
            },
            {
              target: 'closed',
            },
          ],
          MOUSE_LEAVE: 'closed',
          MAP_CLICKED: {
            target: 'visible_from_click',
            guard: 'hasTooltipTarget',
            actions: ['setContext'],
          },
        },
      },

      visible_from_click: {
        on: {
          MAP_CLICKED: [
            {
              target: 'closed',
              actions: ['setContext'],
              guard: 'onEmptySpace',
            },
            {
              target: 'visible_from_click',
              actions: ['setContext'],
              guard: 'tooltipTargetChanged',
            },
            {
              target: 'visible_from_click',
              actions: ['setContext'],
            },
          ],
          MOUSE_MOVE: {
            target: 'visible_from_click',
          },
          UPDATE_POSITION: {
            target: 'visible_from_click',
            actions: ['updateScreenPosition'],
          },
        },
      },

      visible_from_hover: {
        on: {
          MAP_CLICKED: {
            target: 'visible_from_click',
            guard: 'hasTooltipTarget',
            actions: ['setContext'],
          },
          MOUSE_MOVE: [
            {
              target: 'closed',
              guard: 'onEmptySpace',
            },
            {
              target: 'hovering',
              actions: ['setContext'],
              guard: 'tooltipTargetChanged',
            },
          ],
          MOUSE_LEAVE: 'closed',
          MAP_MOVED: 'closed',
        },
      },
    },
  },
  {
    actions: {
      setContext: assign({
        coordinates: ({ event, context }) =>
          event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED'
            ? { lng: event.lng, lat: event.lat }
            : context.coordinates,
        screenPosition: ({ event, context }) =>
          event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED'
            ? { x: event.x, y: event.y }
            : context.screenPosition,
        wavePointName: ({ event }) =>
          event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED'
            ? (event.wavePointName ?? null)
            : null,
        adaptationType: ({ event }) =>
          event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED'
            ? (event.adaptationType ?? null)
            : null,
        buildingOcctype: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') &&
          'buildingOcctype' in event
            ? (event.buildingOcctype ?? null)
            : null,
        buildingCategories: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') &&
          'buildingCategories' in event
            ? (event.buildingCategories ?? null)
            : null,
        buildingProperties: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') &&
          'buildingProperties' in event
            ? (event.buildingProperties ?? null)
            : null,
        buildingSource: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') && 'buildingSource' in event
            ? (event.buildingSource ?? null)
            : null,
        totalDamages: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') && 'totalDamages' in event
            ? (event.totalDamages ?? null)
            : null,
        population: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') && 'population' in event
            ? (event.population ?? null)
            : null,
        floodDepthContour: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') &&
          'floodDepthContour' in event
            ? (event.floodDepthContour ?? null)
            : null,
        floodDepthLabel: ({ event }) =>
          (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') &&
          'floodDepthLabel' in event
            ? (event.floodDepthLabel ?? null)
            : null,
      }),
      updateScreenPosition: assign({
        screenPosition: ({ event, context }) =>
          event.type === 'UPDATE_POSITION'
            ? { x: event.x, y: event.y }
            : (context.screenPosition ?? { x: 0, y: 0 }),
      }),
    },
    guards: {
      onEmptySpace: ({ event }) =>
        (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') &&
        !hasMapTooltipTarget(event),
      hasTooltipTarget: ({ event }) =>
        (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') && hasMapTooltipTarget(event),
      tooltipTargetChanged: ({ event, context }) =>
        (event.type === 'MOUSE_MOVE' || event.type === 'MAP_CLICKED') &&
        (event.wavePointName !== context.wavePointName ||
          event.adaptationType !== context.adaptationType ||
          event.floodDepthContour !== context.floodDepthContour ||
          event.floodDepthLabel !== context.floodDepthLabel),
    },
  }
)
